/**
 * /payroll-l2/crafts — Craft classifications and per-employee time-phased pay rates.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import { api } from "@/lib/api";
import type { Craft } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/crafts")({
  component: CraftsPage,
});

function CraftsPage() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", description: "" });

  const { data: crafts, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "crafts"],
    queryFn: async () => {
      return await api<Craft[]>("/api/payroll-l2/crafts");
    },
  });

  const createMut = useMutation({
    mutationFn: async (body: typeof form) => {
      return await api<Craft>("/api/payroll-l2/crafts", { method: "POST", body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "crafts"] });
      setDialogOpen(false);
      setForm({ code: "", name: "", description: "" });
    },
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Typography variant="h5" fontWeight={600}>Crafts & Rates</Typography>
          <HelpHint docPath="payroll-l2/README.md" label="Crafts help" />
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Add Craft
        </Button>
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load crafts.</Alert>}

      {crafts && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {crafts.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>{c.code}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.description ?? "—"}</TableCell>
                <TableCell>{c.is_active ? "Yes" : "No"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Craft</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              required
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.code.trim() || !form.name.trim() || createMut.isPending}
            onClick={() => createMut.mutate(form)}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
